// src/shared/ui/components/record-row-card.tsx
// One row of a patient's repeating records (a residential address, an emergency contact, a health insurance, a
// corporate agreement) and the list that holds them: the approved design's PaRecordRowCard / PaRecordList, as one shared
// adapter for the four PA row components. Presentational only: every button, handler and field stays with the caller.
//
// Read state: the title with its chips and quiet lines of facts, the caller's actions (edit / remove) on the right.
// Edit state (`editing`): the brand edge, the caller's head actions (cancel), then the fields and the Done / Remove row.
// Look: showcase-patient.css `/* record rows */`, keyed on the `data-slot`s below.
import type { ReactNode } from 'react';
import { Icon } from '@scripsteam/scrips-signal-ds';

export interface RecordRowCardProps {
  title: ReactNode;
  /** Status chips beside the title (Primary, Default for home visits, Verified…). */
  chips?: ReactNode;
  /** Quiet facts under the title (read state only). */
  lines?: ReactNode;
  /** Buttons at the right of the head (edit / remove in read state, cancel in edit state). */
  actions?: ReactNode;
  editing?: boolean;
  /** The fields while editing. */
  children?: ReactNode;
  /** The Done / Remove row while editing (primary first). */
  editActions?: ReactNode;
}

export function RecordRowCard({ title, chips, lines, actions, editing = false, children, editActions }: RecordRowCardProps) {
  return (
    <div data-slot="record-row" data-editing={editing ? 'true' : undefined}>
      <div data-slot="record-row-head">
        <div data-slot="record-row-text">
          <div data-slot="record-row-title-line">
            <span data-slot="record-row-title">{title}</span>
            {chips}
          </div>
          {!editing ? lines : null}
        </div>
        {actions != null ? <span data-slot="record-row-actions">{actions}</span> : null}
      </div>
      {editing ? (
        <div data-slot="record-row-edit">
          {children}
          {editActions != null ? <div data-slot="record-row-edit-actions">{editActions}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

/** One quiet line of facts under a row's title. */
export function RecordRowLine({ children }: { children: ReactNode }) {
  return <span data-slot="record-row-line">{children}</span>;
}

/** The rows of one record section, the empty line when there are none, and the "Add …" row at the foot. */
export function RecordList({ children }: { children: ReactNode }) {
  return <div data-slot="record-list">{children}</div>;
}

export function RecordListEmpty({ children }: { children: ReactNode }) {
  return <p data-slot="record-list-empty">{children}</p>;
}

export function RecordListAdd({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" data-slot="record-list-add" onClick={onClick} disabled={disabled}>
      <Icon name="add" size={16} color="currentColor" aria-hidden />
      {children}
    </button>
  );
}
